import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'reactstrap';
import classNames from 'classnames';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';

import TableFilterComponent from '../../../../shared/components/form/TableFilterComponent';
import TableHeaderLabel from '../../../../shared/components/table/TableHeaderLabel';
import OverflowTitleTd from '../../../../shared/components/table/OverflowTitleTd';
import CommCopyHistoryBlockedPrompt from '../../../../shared/components/table/CommCopyHistoryBlockedPrompt';
import { getAllRefillRequestStatuses } from '../../../../redux/actions/refillRequestActions';
import { getStaffDoctorsByFacility } from '../../../../redux/actions/staffActions';
import { handleRequest } from '../../../../redux/services/APIService';
import {
  REFILL_REQUEST_MESSAGE_STATUS,
  refillRequestDeliveryTypes,
} from '../../../../config/app.config';
import { SortingField } from '../../../common/SortingCell';
import {
  getDefaultSortOrder,
  getSortFieldName,
} from '../../Common/SortBy/utils';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';

import {
  tab,
  useRefillRequestModalContext,
  useStatusChangeModalContext,
} from '../utils';
import { StatusDropdown, getFilteredStatuses } from './StatusDropdown';

const COPIED_BADGE_DURATION_MS = 1500;

export function RefillRequestsTable({
  heads,
  rows,
  handleFilter,
  handleSort,
  sortField,
  sortOrder,
  stickyTop,
  emptyStateMessage = 'No Refill Requests to display',
}) {
  const dispatch = useDispatch();
  const [copyingRefillId, setCopyingRefillId] = useState(null);
  const [copiedRefillId, setCopiedRefillId] = useState(null);
  const [copyBlockedRefillId, setCopyBlockedRefillId] = useState(null);
  const copyBadgeTimeoutRef = useRef(null);

  const allStatuses = useSelector(
    (state) => state.refillRequests.refillRequestStatuses || [],
  );
  const doctors = useSelector(
    (state) => state.staff.staffDoctorsByFacility || [],
  );
  const facilityId = useSelector((state) => state.user?.profile?.facility?._id);
  const {
    openRefillRequestModal,
    handleSetRefillRequestData,
    setIsMessageModalOpen,
  } = useRefillRequestModalContext();
  const { prepareConfirmationMessage } = useStatusChangeModalContext();

  useEffect(() => {
    if (facilityId) {
      dispatch(getAllRefillRequestStatuses({ facilityId }));
      dispatch(getStaffDoctorsByFacility());
    }
  }, [dispatch, facilityId]);

  useEffect(() => {
    return () => {
      if (copyBadgeTimeoutRef.current) {
        clearTimeout(copyBadgeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!copyBlockedRefillId) {
      return undefined;
    }

    const dismissBlockedPrompt = () => {
      setCopyBlockedRefillId(null);
    };

    document.addEventListener('mousedown', dismissBlockedPrompt);
    return () => {
      document.removeEventListener('mousedown', dismissBlockedPrompt);
    };
  }, [copyBlockedRefillId]);

  const statusOrder = ['Requested', 'In the queue', 'Ready for pickup', 'Shipped'];

  const handleStatusFilter = (key, list) => {
    const filterField = key === 'doctorFullName' ? 'doctorId' : key;

    handleFilter(filterField, list);
  };

  const handleCopyHistory = async (event, row) => {
    event.preventDefault();
    event.stopPropagation();

    if (copyingRefillId) {
      return;
    }

    setCopyBlockedRefillId(null);
    setCopyingRefillId(row.id);

    try {
      const response = await handleRequest(
        'get',
        `prescription/${row.id}/messages`,
        true,
      );
      const messages = response.data || [];

      const text = buildMessageHistoryTranscript({
        requestType: 'Refill Request',
        header: {
          parent: row.parentFullName,
          pet: row.petName,
          doctor: row.doctorFullName,
          medication: row.medication,
          quantity: row.quantity,
          dosage: row.dosage,
          submitted: row.dateTime || `${row.date || ''} ${row.time || ''}`.trim(),
        },
        messages,
      });

      try {
        await navigator.clipboard.writeText(text);
      } catch (clipboardError) {
        console.error(clipboardError);
        setCopyBlockedRefillId(row.id);
        setCopyingRefillId(null);
        return;
      }

      if (copyBadgeTimeoutRef.current) {
        clearTimeout(copyBadgeTimeoutRef.current);
      }

      setCopiedRefillId(row.id);
      setCopyingRefillId(null);

      copyBadgeTimeoutRef.current = setTimeout(() => {
        setCopiedRefillId(null);
        copyBadgeTimeoutRef.current = null;
      }, COPIED_BADGE_DURATION_MS);
    } catch (error) {
      console.error(error);
      setCopyingRefillId(null);
    }
  };

  const hasParent = heads.some(
    (head) => head && head.key === 'parentFullName',
  );
  const tableClassName = classNames(
    'custom-table',
    'loyal-table',
    'fixed-layout',
    {
      'refill-request-table--no-parent': !hasParent,
    },
  );

  const renderRowValues = (row) => {
    const dropdownStatuses = getFilteredStatuses(row.deliveryType, allStatuses);
    const sortedStatuses = dropdownStatuses.sort(
      (a, b) => statusOrder.indexOf(a.label) - statusOrder.indexOf(b.label),
    );

    const statusColumn = (head, currentStatus) => (
      <td
        key={head.key}
        className={head.className ?? ''}
        onClick={(e) => e.stopPropagation()}
      >
        <StatusDropdown
          currentStatus={currentStatus}
          statuses={sortedStatuses}
          onStatusChange={(newStatus) =>
            prepareConfirmationMessage({
              currentStatus,
              newStatus: newStatus.label,
              newStatusId: newStatus._id,
              prescriptionId: row.id,
            })
          }
          className="refill-requests-dropdown"
        />
      </td>
    );

    const attachmentsAndNotesColumns = (head) => {
      const tabToOpen = {
        attachments: tab.ATTACHMENTS,
        internalComments: tab.INTERNAL_COMMENTS,
      };

      const isButtonVisible = {
        attachments: !!row.documents?.length || !!row.photos?.length,
        internalComments: !!row.staffInternalComments?.length,
      };

      return (
        <td
          key={head.key}
          className={classNames('custom-no-over-flow-td', head.className)}
          onClick={(e) => {
            isButtonVisible[head.key] && e.stopPropagation();
          }}
          style={{ textAlign: 'center' }}
        >
          {isButtonVisible[head.key] && (
            <a
              style={{ padding: 0 }}
              className="view-tab-button full-width"
              onClick={() => {
                openRefillRequestModal(tabToOpen[head.key]);
                handleSetRefillRequestData(row);
              }}
            >
              View
            </a>
          )}
        </td>
      );
    };

    const renderMessageColumn = (head) => (
      <td
        key={head.key}
        className={head.className ?? ''}
        onClick={(e) => e.stopPropagation()}
      >
        {!row.isReplyExpired && (
          <button
            type="button"
            className="nav-link-style message-button"
            onClick={() => {
              handleSetRefillRequestData(row);
              setIsMessageModalOpen(true);
            }}
          >
            Message
          </button>
        )}
      </td>
    );

    const renderMessageStatusColumn = (head, value) => {
      let dotClass = 'none';
      let columnText = '–';

      switch (value) {
        case REFILL_REQUEST_MESSAGE_STATUS.AWAIT_REPLY:
          dotClass = 'yellow';
          columnText = value;
          break;

        case REFILL_REQUEST_MESSAGE_STATUS.NEW_REPLY:
          dotClass = 'blue';
          columnText = value;
          break;

        default:
          dotClass = 'none';
          columnText = '–';
          break;
      }

      return (
        <td key={head.key} className={head.className ?? ''}>
          <div className="message-status text-capitalize">
            <span className={classNames('dot', dotClass)}></span>
            <span>{columnText}</span>
          </div>
        </td>
      );
    };

    const renderCopyHistoryColumn = (head) => {
      const isCopying = copyingRefillId === row.id;
      const showCopiedBadge = copiedRefillId === row.id;
      const showBlockedPrompt = copyBlockedRefillId === row.id;

      return (
        <td
          key={head.key}
          className={classNames('custom-no-over-flow-td', head.className)}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="comm-copy-history-wrap">
            <button
              type="button"
              className="comm-copy-history-btn"
              title="Copy history"
              disabled={isCopying}
              onClick={(event) => handleCopyHistory(event, row)}
            >
              <ContentCopyIcon size={16} aria-hidden />
            </button>
          </span>
          {showBlockedPrompt ? (
            <CommCopyHistoryBlockedPrompt recordLabel="Refill Request" />
          ) : null}
          {showCopiedBadge ? (
            <span className="comm-copy-history-copied-badge">Copied!</span>
          ) : null}
        </td>
      );
    };

    const defaultColumn = (head, value, capitalizeClass) => {
      const columnText =
        value === 'delivery' ? refillRequestDeliveryTypes.delivery : value;

      return (
        <OverflowTitleTd
          key={head.key}
          titleText={columnText}
          className={classNames(
            'custom-no-over-flow-td',
            capitalizeClass,
            head.className,
          )}
        >
          {columnText}
        </OverflowTitleTd>
      );
    };

    return heads.map((head) => {
      const value = row[head.key];
      const capitalizeClass = head.capitalize ? 'text-capitalize' : '';

      if (head.key === 'copyHistory') {
        return renderCopyHistoryColumn(head);
      }

      if (head.key === 'message') {
        return renderMessageColumn(head);
      }

      if (head.key === 'messageStatus') {
        return renderMessageStatusColumn(head, value);
      }

      if (head.key === 'status' && allStatuses.length !== 0) {
        return statusColumn(head, value);
      }

      if (head.key === 'attachments' || head.key === 'internalComments') {
        return attachmentsAndNotesColumns(head);
      }

      return defaultColumn(head, value, capitalizeClass);
    });
  };

  const renderTableHeaders = () => {
    const renderSortableHeader = (head) => {
      const sortKey = getSortFieldName(head.key);
      const defaultOrder = getDefaultSortOrder(sortKey);

      return (
        <th key={head.key} className={head.className ?? ''}>
          {sortField === sortKey ? (
            <SortingField
              icon={sortOrder === 1 ? 'triangle-up' : 'triangle-down'}
              name={head.name}
              onSort={() => handleSort({ order: sortOrder === 1 ? -1 : 1 })}
            />
          ) : (
            <SortingField
              name={head.name}
              icon="diamond-narrow"
              onSort={() => handleSort({ field: sortKey, order: defaultOrder })}
            />
          )}
        </th>
      );
    };

    const renderFilterableHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <TableFilterComponent
          allFilters={head.filterValue}
          action={(list) => {
            handleStatusFilter(head.key, list);
          }}
          label={head.name}
          labelTooltip={head.headerTooltip}
          labelTooltipId={`table-header-info-${head.key}`}
          labelTooltipClassName={head.headerTooltipClassName}
        />
      </th>
    );

    const renderIconHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <img src={head.imgSrc} alt={head.imgAlt} title={head.name} />
      </th>
    );

    const renderDefaultHeader = (head) => (
      <th key={head.key} className={head.className ?? ''}>
        <TableHeaderLabel
          id={`table-header-info-${head.key}`}
          label={head.name}
          tooltip={head.headerTooltip}
          tooltipClassName={head.headerTooltipClassName}
        />
      </th>
    );

    const renderHeader = (head) => {
      if (!head) return null;

      if (head.sortable) {
        return renderSortableHeader(head);
      } else if (head.filterable) {
        if (head.key === 'status') {
          head.filterValue = allStatuses
            .sort(
              (a, b) =>
                statusOrder.indexOf(a.label) - statusOrder.indexOf(b.label),
            )
            .map(({ label }) => ({
              [label]: label,
            }));
        }
        if (head.key === 'doctorFullName') {
          if (doctors.length === 0) return renderDefaultHeader(head);

          const filterValues = doctors.map(({ _id, name }) => ({
            [_id]: `Dr. ${name}`,
          }));

          filterValues.push({ NA: 'N/A' });

          head.filterValue = filterValues;
        }

        return renderFilterableHeader(head);
      } else if (head.imgSrc) {
        return renderIconHeader(head);
      } else {
        return renderDefaultHeader(head);
      }
    };

    return (
      <tr className="header-tr" style={stickyTop ? { top: stickyTop } : {}}>
        {heads.map((head) => renderHeader(head))}
      </tr>
    );
  };

  return (
    <div className="row">
      <div className="col-md-12">
        <Table striped className={tableClassName}>
          <thead>{renderTableHeaders()}</thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                className="body-tr refill-request-row"
                key={index}
                onClick={() => {
                  openRefillRequestModal();
                  handleSetRefillRequestData(row);
                }}
              >
                {renderRowValues(row)}
              </tr>
            ))}
          </tbody>
        </Table>

        {rows.length === 0 ? (
          <div className="no-data-message">{emptyStateMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
